import React from 'react';
import { arrayOf } from 'prop-types';
import { productShape } from 'shapes';
import { useBaseUrls, useEnvironmentData, useCategory } from 'hooks';

export default function SeoProductsMicrodata({ hits }) {
    const { platform, cdn } = useEnvironmentData();
    const baseUrls = useBaseUrls();
    const category = useCategory();

    const aggregatedData = hits.reduce(
        (data, hit) => {
            if (hit.rating_count === 0) return data;

            return {
                maxPrice: Math.max(data.maxPrice, hit.price),
                minPrice: Math.min(data.minPrice, hit.price),
                ratingCount: data.ratingCount + hit.rating_count,
                ratingValuesLength: data.ratingValuesLength + 1,
                ratingValuesSum: data.ratingValuesSum + hit.rating,
            };
        },
        {
            maxPrice: 0,
            minPrice: Number.POSITIVE_INFINITY,
            ratingCount: 0,
            ratingValuesLength: 0,
            ratingValuesSum: 0,
        },
    );

    if (aggregatedData.ratingValuesLength === 0) {
        // if there are no ratings we don't display the microdata
        return null;
    }

    let categoryUrl = '';
    if (category.context.breadcrumb?.length > 0) {
        categoryUrl = `${baseUrls.main}${
            category.context.breadcrumb[category.context.breadcrumb.length - 1]
                .url
        }`;
    }

    const microdata = {
        '@context': 'https://schema.org/',
        '@id': '#product',
        '@type': 'Product',
        name: `${category.name}`,
        image: `${cdn.CDN}${hits[0].image_path}`, // to replace with the proper category image
        url: categoryUrl,
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue:
                aggregatedData.ratingValuesSum /
                aggregatedData.ratingValuesLength,
            bestRating: 5,
            ratingCount: aggregatedData.ratingCount,
        },
        offers: {
            '@type': 'AggregateOffer',
            highPrice: aggregatedData.maxPrice,
            lowPrice: aggregatedData.minPrice,
            priceCurrency: platform.IS_PLATFORM_UK ? 'GBP' : 'EUR',
        },
    };

    return (
        <script
            type="application/ld+json"
            data-qa="structuredDataSEOSomeProductsElm"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(microdata) }}
        />
    );
}

SeoProductsMicrodata.propTypes = {
    hits: arrayOf(productShape).isRequired,
};
